import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Mail, RotateCcw } from "lucide-react";
import { BrandHeader } from "@/components/BrandHeader";
import { ScoreBars } from "@/components/ScoreBars";
import { ReportSection } from "@/components/ReportSection";
import { EmailResults } from "@/components/EmailResults";
import { GuideCard } from "@/components/GuideCard";
import { WaitlistDialog } from "@/components/WaitlistDialog";
import { WaitlistContext } from "@/lib/waitlist";
import { ASSETS } from "@/lib/assets";
import { API } from "@/App";

const Actions = ({ pos }) => (
  <div className="flex flex-col sm:flex-row gap-3 no-print">
    <button onClick={() => window.print()} className="fea-btn" data-testid={`download-results-button-${pos}`}>
      <Download className="h-5 w-5" /> Download My Results
    </button>
    <a href="#email-results" className="fea-btn-outline" data-testid={`email-results-jump-button-${pos}`}>
      <Mail className="h-5 w-5" /> Email My Results
    </a>
  </div>
);

export default function Results({ result, consentText, onRestart }) {
  const { primary, secondary } = result;
  const [ready, setReady] = useState({});
  const [active, setActive] = useState(null);
  useEffect(() => {
    axios.get(`${API}/waitlist/forms`).then((r) => setReady(r.data.ready)).catch(() => setReady({}));
  }, []);
  const date = new Date(result.completed_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  return (
    <WaitlistContext.Provider value={{ ready, open: setActive, resultId: result.id }}>
    <div data-testid="results-screen">
      <BrandHeader right={<button onClick={onRestart} className="fea-btn-ghost no-print text-sm" data-testid="retake-button"><RotateCcw className="h-4 w-4" /> Retake</button>} />
      <main className="fea-container pt-6 sm:pt-10">
        <section className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-center fea-rise">
          <img src={ASSETS.results} alt="Your Feminine Energy Profile" className="w-full rounded-[28px] object-cover max-h-[70vh] shadow-[0_24px_60px_-30px_rgba(128,5,52,0.45)] fea-print-image" data-testid="results-image" />
          <div>
            <p className="fea-overline">Your Feminine Energy Assessment™ · {date}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#800534] mt-3 leading-[1.05]" data-testid="primary-profile-name">
              The {primary.profile}
            </h1>
            <p className="font-serif italic text-lg text-[#3B121A] mt-3" data-testid="primary-profile-tagline">{primary.tagline}</p>
            <p className="fea-body mt-4">Secondary energy concentration: <strong className="text-[#800534]" data-testid="secondary-profile-name">{secondary.profile}</strong></p>
            <div className="fea-card !p-6 mt-6"><ScoreBars scores={result.scores} primary={primary.category} secondary={secondary.category} /></div>
            <div className="mt-6"><Actions pos="top" /></div>
          </div>
        </section>
        <div className="mt-14 space-y-6" data-testid="report-sections">
          {result.sections.map((s) => <ReportSection key={s.letter} s={s} />)}
        </div>
        <div className="mt-6"><GuideCard /></div>
        <div className="mt-6"><EmailResults resultId={result.id} consentText={consentText} /></div>
        <p className="text-sm text-[#9E7381] mt-10 leading-relaxed" data-testid="results-disclaimer">{result.disclaimer}</p>
        <div className="mt-8 no-print"><Actions pos="bottom" /></div>
      </main>
      <footer className="fea-footer">© Goddess University · Founded by Rev. Goddess LaMeel</footer>
      <WaitlistDialog cta={active} resultId={result.id} onClose={() => setActive(null)} />
    </div>
    </WaitlistContext.Provider>
  );
}
